// Decisiones de la vista «Historial de conducción» que no dependen de React.
// Regla de §3.1: si un dato no existe, no se dibuja (ni ceros ni guiones).

export interface HistoryTotals {
  driving_min: number;
  traffic_min: number;
  stopped_min: number;
  idle_min: number | null;
  idle_estimated_min: number;
  rest_min: number;
  nodata_min: number;
  km: number;
  events: number;
  points: number;
}

export interface HistoryTelemetry {
  temp_max_c: number | null;
  volt_min_v: number | null;
  rpm_max: number | null;
  fuel_pct: number | null;
}

export interface HistoryBlock {
  from_hour: number;
  driving_min: number;
  stopped_min: number;
}

export interface HistoryHour {
  hour: number;
  avg_speed_kmh: number | null;
  events: number;
}

export interface HistoryScore {
  index: number | null;
  color: "green" | "yellow" | "red" | "gray";
  breakdown: { type: string; count: number; pct: number }[];
}

export interface HistoryDay {
  date: string;
  has_data: boolean;
  totals: HistoryTotals | null;
  telemetry: HistoryTelemetry;
  score: HistoryScore;
  blocks: HistoryBlock[];
  hourly: HistoryHour[];
}

export interface DrivingHistoryResponse {
  unit: { device_id: string; name: string | null; plate: string | null; driver: { name: string | null } | null };
  from: string;
  to: string;
  tz: string;
  days: HistoryDay[];
  period: {
    totals: HistoryTotals;
    telemetry: HistoryTelemetry;
    blocks: HistoryBlock[];
    hourly: HistoryHour[];
    score: HistoryScore;
  } | null;
  captures: unknown[];
}

/** "X h MM min"; menos de una hora, "M min". */
export const fmtMinutes = (min: number): string => {
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, "0")} min`;
};

export interface Tile {
  key: string;
  label: string;
  value: string;
  icon: string;
}

export const totalTiles = (t: HistoryTotals): Tile[] => {
  const tiles: Tile[] = [
    { key: "driving", label: "Al volante", value: fmtMinutes(t.driving_min), icon: "ri-steering-2-line" },
    { key: "km", label: "Kilómetros", value: `${Math.round(t.km)} km`, icon: "ri-road-map-line" },
    { key: "events", label: "Eventos", value: String(t.events), icon: "ri-alarm-warning-line" },
  ];
  if (t.idle_min !== null) tiles.push({ key: "idle", label: "Ralentí", value: fmtMinutes(t.idle_min), icon: "ri-pause-circle-line" });
  tiles.push(
    { key: "traffic", label: "Tráfico", value: fmtMinutes(t.traffic_min), icon: "ri-traffic-light-line" },
    { key: "rest", label: "Descanso", value: fmtMinutes(t.rest_min), icon: "ri-cup-line" },
  );
  return tiles;
};

export const telemetryTiles = (t: HistoryTelemetry): Tile[] => {
  const out: Tile[] = [];
  if (t.temp_max_c !== null) out.push({ key: "temp", label: "Temp. máx.", value: `${Math.round(t.temp_max_c)} °C`, icon: "ri-temp-hot-line" });
  if (t.volt_min_v !== null) out.push({ key: "volt", label: "Voltaje mín.", value: `${t.volt_min_v.toFixed(1)} V`, icon: "ri-flashlight-line" });
  if (t.rpm_max !== null) out.push({ key: "rpm", label: "RPM máx.", value: String(Math.round(t.rpm_max)), icon: "ri-dashboard-3-line" });
  if (t.fuel_pct !== null) out.push({ key: "fuel", label: "Combustible", value: `${t.fuel_pct}%`, icon: "ri-gas-station-line" });
  return out;
};

const BEHAVIOR_LABELS: Record<string, string> = {
  microsleep: "Microsueño",
  fatigue: "Fatiga",
  phone: "Celular",
  camera_covered: "Cámara tapada",
  distraction: "Distracción",
  forward_collision: "Colisión frontal",
  tailgating: "Distancia corta",
  lane_departure: "Salida de carril",
};
export const behaviorLabel = (type: string): string => BEHAVIOR_LABELS[type] ?? type;

/** El índice y su desglose sólo existen si la cámara detectó conductas (§3.3). */
export const showScore = (s: HistoryScore | undefined | null): boolean => s != null && s.index !== null;

/** "2026-10-15" → "15/10/2026" sin pasar por Date (evita el corrimiento de zona). */
export const fmtDay = (d: string): string => d.split("-").reverse().join("/");

export const todayIn = (tz: string, now = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
